import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { getTranslation } from '../data/translations';
import { mockProducts, mockCategories, whatsappConfig } from '../data/mockData';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { MessageCircle, Grid3x3, List, Mail } from 'lucide-react';

const Products = () => {
  const { language } = useLanguage();
  const t = (key) => getTranslation(language, key);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');

  const filteredProducts =
    selectedCategory === 'all'
      ? mockProducts
      : mockProducts.filter((p) => p.category[language] === selectedCategory);

  const handleInquiry = (productName) => {
    const subject = encodeURIComponent(`Product Inquiry: ${productName}`);
    const body = encodeURIComponent(`Hello Taj Gulf Trading LLC,\n\nI would like to inquire about: ${productName}\n\nPlease provide more information.\n\nThank you.`);
    window.location.href = `mailto:sales@tajgulfoman.com?subject=${subject}&body=${body}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#1a3a52] to-[#2a4a62]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            {t('products.title')}
          </h1>
          <p className="text-xl text-gray-200">
            {t('products.subtitle')}
          </p>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
            <h2 className="text-2xl font-bold text-[#1a3a52] mb-4 md:mb-0">
              {t('products.categories')}
            </h2>
            <div className="flex space-x-2">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className={viewMode === 'grid' ? 'bg-[#1a3a52]' : ''}
              >
                <Grid3x3 className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('list')}
                className={viewMode === 'list' ? 'bg-[#1a3a52]' : ''}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              onClick={() => setSelectedCategory('all')}
              className={`transition-all duration-200 ${
                selectedCategory === 'all'
                  ? 'bg-[#1a3a52] hover:bg-[#0f2535]'
                  : 'hover:border-[#1a3a52] hover:text-[#1a3a52]'
              }`}
            >
              {language === 'en' ? 'All Products' : 'جميع المنتجات'}
            </Button>
            {mockCategories.map((category) => (
              <Button
                key={category.id}
                variant={selectedCategory === category.name[language] ? 'default' : 'outline'}
                onClick={() => setSelectedCategory(category.name[language])}
                className={`transition-all duration-200 ${
                  selectedCategory === category.name[language]
                    ? 'bg-[#1a3a52] hover:bg-[#0f2535]'
                    : 'hover:border-[#1a3a52] hover:text-[#1a3a52]'
                }`}
              >
                {category.name[language]} ({category.count})
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={
              viewMode === 'grid'
                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8'
                : 'flex flex-col space-y-6'
            }
          >
            {filteredProducts.map((product) => (
              <Card
                key={product.id}
                className={`overflow-hidden border-none shadow-lg hover:shadow-2xl transition-all duration-300 ${
                  viewMode === 'grid' ? 'hover:-translate-y-2' : ''
                } bg-white`}
              >
                {viewMode === 'grid' ? (
                  <>
                    <div className="aspect-square overflow-hidden bg-gray-100">
                      <img
                        src={product.image}
                        alt={product.name[language]}
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <CardContent className="p-6">
                      <div className="mb-3">
                        <span className="inline-block bg-[#c5a365]/10 text-[#c5a365] text-xs font-semibold px-3 py-1 rounded-full">
                          {product.category[language]}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-[#1a3a52] mb-2">
                        {product.name[language]}
                      </h3>
                      <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                        {product.description[language]}
                      </p>
                      <Button
                        onClick={() => handleInquiry(product.name[language])}
                        className="w-full bg-[#1a3a52] hover:bg-[#0f2535] text-white transition-all duration-200"
                      >
                        <Mail className="h-4 w-4 mr-2" />
                        {t('products.inquiry')}
                      </Button>
                    </CardContent>
                  </>
                ) : (
                  <div className="flex flex-col md:flex-row">
                    <div className="md:w-1/3 h-64 md:h-auto overflow-hidden bg-gray-100">
                      <img
                        src={product.image}
                        alt={product.name[language]}
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <CardContent className="md:w-2/3 p-8 flex flex-col justify-between">
                      <div>
                        <div className="mb-3">
                          <span className="inline-block bg-[#c5a365]/10 text-[#c5a365] text-xs font-semibold px-3 py-1 rounded-full">
                            {product.category[language]}
                          </span>
                        </div>
                        <h3 className="text-2xl font-bold text-[#1a3a52] mb-3">
                          {product.name[language]}
                        </h3>
                        <p className="text-gray-600 mb-6 leading-relaxed">
                          {product.description[language]}
                        </p>
                      </div>
                      <Button
                        onClick={() => handleInquiry(product.name[language])}
                        className="bg-[#1a3a52] hover:bg-[#0f2535] text-white w-full md:w-auto transition-all duration-200"
                      >
                        <Mail className="h-4 w-4 mr-2" />
                        {t('products.inquiry')}
                      </Button>
                    </CardContent>
                  </div>
                )}
              </Card>
            ))}
          </div>

          {/* More Products CTA */}
          <div className="mt-16 text-center bg-gradient-to-r from-[#1a3a52] to-[#2a4a62] rounded-2xl p-12">
            <h3 className="text-3xl font-bold text-white mb-4">
              {language === 'en' ? 'Looking for Something Specific?' : 'تبحث عن شيء محدد؟'}
            </h3>
            <p className="text-gray-200 text-lg mb-6">
              {language === 'en'
                ? 'We have 1000+ products available. Contact us for your specific requirements.'
                : 'لدينا أكثر من 1000 منتج متاح. اتصل بنا لمتطلباتك المحددة.'}
            </p>
            <Button
              onClick={() => handleInquiry('Custom Product Inquiry')}
              size="lg"
              className="bg-[#c5a365] hover:bg-[#b39355] text-white px-8 py-6 text-lg font-semibold transition-all duration-300 hover:scale-105"
            >
              <Mail className="h-5 w-5 mr-2" />
              {t('nav.contact')}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
